import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { SIZES, THEME } from "app/config/constants";
import { settingsData } from "app/config/settingsidebar";
import { Link } from "react-router-dom";

// Header component
function SettingsModalHeader({
  search,
  setSearch,
  maximized,
  setMaximized,
  onClose,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 14px",
        background: THEME.white,
        borderBottom: "1px solid rgba(0,0,0,0.1)",
        minHeight: 48,
      }}
    >
      {/* Left: Setup label */}
      <Box
        sx={{
          minWidth: 90,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#084c84",
            letterSpacing: 1,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Setup
        </Typography>
      </Box>
      {/* Center: Search */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Search settings"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: maximized ? 320 : 180,
            padding: "7px 14px",
            borderRadius: 6,
            border: "1px solid #e0e0e0",
            fontSize: 15,
            outline: "none",
            background: "#f7fafd",
            color: "#222",
            transition: "width 0.3s",
          }}
        />
      </Box>
      {/* Right: Maximize/Minimize and Close */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
        <IconButton
          onClick={() => setMaximized((m) => !m)}
          sx={{
            color: "#084c84",
            "&:hover": { backgroundColor: "rgba(8, 76, 132, 0.1)" },
          }}
          aria-label={maximized ? "Minimize" : "Maximize"}
        >
          {maximized ? (
            <CloseFullscreenIcon fontSize="medium" />
          ) : (
            <OpenInFullIcon fontSize="medium" />
          )}
        </IconButton>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#084c84",
            "&:hover": { backgroundColor: "rgba(8, 76, 132, 0.1)" },
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

// Card List component
function SettingsCardList({ filtered, maximized, onClose }) {
  return (
    <Grid
      container
      spacing={2}
      justifyContent={maximized ? "flex-start" : "center"}
    >
      {filtered.length === 0 && (
        <Grid item xs={12}>
          <Typography sx={{ color: "#888", textAlign: "center", mt: 6 }}>
            No settings found.
          </Typography>
        </Grid>
      )}
      {filtered.map((category, index) =>
        maximized ? (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <SettingsCard
              category={category}
              onClose={onClose}
              maximized={maximized}
            />
          </Grid>
        ) : (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            key={index}
            sx={{ maxWidth: 400, width: "100%" }}
          >
            <SettingsCard
              category={category}
              onClose={onClose}
              maximized={maximized}
            />
          </Grid>
        )
      )}
    </Grid>
  );
}

// Card component
function SettingsCard({ category, onClose, maximized }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        background: THEME.white,
        borderRadius: 2,
        height: "100%",
        boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
        border: "1px solid #eee",
        display: "flex",
        flexDirection: "column",
        minWidth: 210,
        maxWidth: maximized ? 260 : 400,
        margin: "0 auto",
      }}
    >
      <Box
        sx={{
          padding: "10px 15px",
          borderBottom: "1px solid #eee",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", color: "#666" }}
        >
          {category.label}
        </Typography>
      </Box>
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          p: 0,
          m: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {(category.children || [category]).map((item, itemIndex) => (
          <SettingsCardItem item={item} onClose={onClose} key={itemIndex} />
        ))}
      </Box>
    </Paper>
  );
}

// Card Item component
function SettingsCardItem({ item, onClose }) {
  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        alignItems: "stretch",
        borderRadius: 1,
        color: "#333",
        background: "transparent",
        transition: "color 0.2s, background-color 0.2s",
        cursor: "pointer",

        p: 0,
        "&:hover": {
          color: "#084c84",
          backgroundColor: "#f5f6f7",
          ".launch-btn": {
            opacity: 1,
            pointerEvents: "auto",
          },
        },
      }}
    >
      <Link
        to={item.link}
        onClick={onClose}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          textDecoration: "none",
          color: "inherit",
          background: "transparent",
          borderRadius: "4px 0 0 4px",
          fontSize: 15,
          height: "100%",
        }}
      >
        {item.label}
      </Link>
      <IconButton
        className="launch-btn"
        size="small"
        sx={{
          opacity: 0,
          pointerEvents: "none",
          background: "#e5e6e8",
          borderRadius: "0 4px 4px 0",
          transition: "opacity 0.2s, background 0.2s",
          height: "100%",
          width: 40,
          minWidth: 40,
          maxWidth: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "none",
          margin: 0,
          padding: 0,
          "&:hover": {
            background: "#e0e0e0",
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          let url =
            item.link && item.link.startsWith("/")
              ? item.link
              : `/${item.link?.replace(/^\/+/, "")}`;
          if (item.link) window.open(url, "_blank", "noopener,noreferrer");
        }}
      >
        <LaunchIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const SettingsModal = ({ open, onClose }) => {
  const [search, setSearch] = useState("");
  const [maximized, setMaximized] = useState(false);

  // Regex filter for cards and sub-items
  let filtered = settingsData
    .map((category) => {
      if (!search) return category;
      const regex = (() => {
        try {
          return new RegExp(search, "i");
        } catch {
          return null;
        }
      })();
      if (!regex) return null;
      if (regex.test(category.label)) return category;
      if (category.children) {
        const filteredChildren = category.children.filter((child) =>
          regex.test(child.label)
        );
        if (filteredChildren.length)
          return { ...category, children: filteredChildren };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: maximized ? "100vw" : 320,
          minWidth: maximized ? undefined : 400,
          maxWidth: maximized ? undefined : 500,
          padding: 0,
          top: SIZES.desktopNavbarHeight,
          height: `calc(100vh - ${SIZES.desktopNavbarHeight})`,
          zIndex: 1000,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          background: "#f4f7f9",
          transition: "width 0.3s cubic-bezier(.4,2,.6,1)",
        },
      }}
      hideBackdrop={true}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <SettingsModalHeader
          search={search}
          setSearch={setSearch}
          maximized={maximized}
          setMaximized={setMaximized}
          onClose={onClose}
        />
        <Box sx={{ flex: 1, padding: 3, overflowY: "auto" }}>
          <SettingsCardList
            filtered={filtered}
            maximized={maximized}
            onClose={onClose}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default SettingsModal;
